import { useInkind } from '@gik/api/inkinds/inkind';
import { removeInkindCoverPhoto } from '@gik/api/inkinds/inkindCoverPhoto';
import { patchInkind } from '@gik/api/inkinds/inkinds';
import { optimisticUpdate } from '@gik/core/api';
import { useBemCN } from '@gik/core/utils/bemBlock';
import sleep from '@gik/core/utils/sleep';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { colorNameTranslationPrefix, translationKeys } from '@gik/inkind-page/i18n/en';
import { Button } from '@gik/ui/Button';
import { CoverSettings } from '@gik/ui/SvgIcon/GikIcons/CoverSettings';
import { SvgIcon } from '@gik/ui/SvgIcon/SvgIcon';
import CameraIcon from '@heroicons/react/solid/CameraIcon';
import CloseIcon from '@heroicons/react/solid/XIcon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useClickAway from 'react-use/lib/useClickAway';
import usePrevious from 'react-use/lib/usePrevious';

interface IInkindCoverToolbarProps {
  inkindRouteId: string;
  onClickAddCoverPhoto(): void;

  onCoverColourChange(): void;
}

interface IInkindCoverToolbarButtonProps {
  onClick: () => void;
  icon?: SvgrComponent;
  className?: string;
  tooltip?: string;
  isClosingToolbar?: boolean;
}

function InkindCoverToolbarButtonComp({
  onClick,
  icon,
  className,
  tooltip,
  isClosingToolbar,
}: IInkindCoverToolbarButtonProps) {
  const bem = useBemCN('inkind-cover-toolbar-button');

  return (
    <div {...bem(undefined, [{ closing: isClosingToolbar }])}>
      <Button
        type="button"
        variant="secondary"
        noFocusStyling
        circle
        tooltip={tooltip}
        tooltipClassName={bem('tooltip').className}
        {...bem('button', undefined, className)}
        onClick={() => onClick()}
      >
        {icon && <SvgIcon Icon={icon} />}
      </Button>
    </div>
  );
}

const InkindCoverToolbarButton = React.memo(InkindCoverToolbarButtonComp, (prevProps, nextProps) => {
  return nextProps.isClosingToolbar === prevProps.isClosingToolbar;
});

function InkindCoverToolbarComp({
  inkindRouteId,
  onClickAddCoverPhoto,
  onCoverColourChange,
  ...otherProps
}: IInkindCoverToolbarProps) {
  const { data: inkindPage, mutate: mutateInkindPage } = useInkind(inkindRouteId);
  const [isToolbarOpen, setToolbarOpen] = React.useState(false);
  const prevIsToolbarOpen = usePrevious(isToolbarOpen);
  const [isClosingToolbar, setIsClosingToolbar] = React.useState(false);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (prevIsToolbarOpen && !isToolbarOpen) {
      (async () => {
        setIsClosingToolbar(true);
        await sleep(1000);
        setIsClosingToolbar(false);
      })();
    }
  }, [prevIsToolbarOpen, isToolbarOpen, setIsClosingToolbar]);

  const toolbarRef = React.useRef(null);
  useClickAway(toolbarRef, () => {
    setToolbarOpen(false);
  });
  const bem = useBemCN('inkind-cover-toolbar');
  const hasPhoto = !!inkindPage?.cover_src;

  async function removeCoverPhoto() {
    optimisticUpdate(
      {
        ...inkindPage,
        cover_src: null,
        cover_src_retina: null,
      },
      null,
      mutateInkindPage,
      () => removeInkindCoverPhoto(inkindRouteId)
    );

    setToolbarOpen(false);
  }

  async function selectGradient(coverColourIndex: number) {
    optimisticUpdate(
      {
        ...inkindPage,
        coverColourIndex,
      },
      null,
      mutateInkindPage,
      () => patchInkind(inkindRouteId, { coverColourIndex })
    );

    setToolbarOpen(false);
    onCoverColourChange();
  }

  const handleToolbarButtonClick = React.useCallback(() => {
    setToolbarOpen(!isToolbarOpen);
  }, [isToolbarOpen]);

  return (
    <div ref={toolbarRef} {...bem()} {...otherProps}>
      <InkindCoverToolbarButton
        onClick={handleToolbarButtonClick}
        icon={CoverSettings}
        tooltip={t(translationKeys.PageHeaderChangeBackground)}
      />
      {(isToolbarOpen || isClosingToolbar || prevIsToolbarOpen) && (
        <>
          <InkindCoverToolbarButton
            isClosingToolbar={isClosingToolbar}
            onClick={hasPhoto ? removeCoverPhoto : onClickAddCoverPhoto}
            icon={hasPhoto ? CloseIcon : CameraIcon}
            tooltip={t(hasPhoto ? translationKeys.PageHeaderRemovePhoto : translationKeys.PageHeaderChoosePhoto)}
          />
          {Array.from(Array(6).keys()).map(gradientIndex => (
            <InkindCoverToolbarButton
              isClosingToolbar={isClosingToolbar}
              key={gradientIndex}
              onClick={() => selectGradient(gradientIndex)}
              className={`gradient${gradientIndex}`}
              tooltip={t(translationKeys[colorNameTranslationPrefix + gradientIndex])}
            />
          ))}
        </>
      )}
    </div>
  );
}

export const InkindCoverToolbar = withComponentErrorBoundary(React.memo(InkindCoverToolbarComp));
